import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'

interface ParallaxProps {
    children: ReactNode
    /** Percentage the inner layer travels in each direction while the frame crosses the viewport. */
    amount?: number
    className?: string
    innerClassName?: string
}

/**
 * A clipped frame whose content drifts against the scroll.
 * The inner layer is oversized so the drift never reveals an edge.
 */
const Parallax: FC<ParallaxProps> = ({ children, amount = 12, className, innerClassName }): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const inner = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        if (!inner.current || !root.current || prefersReducedMotion()) return

        gsap.fromTo(inner.current, { yPercent: -amount }, {
            yPercent: amount,
            ease: 'none',
            scrollTrigger: {
                trigger: root.current,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
            }
        })
    }, { scope: root, dependencies: [amount] })

    return (
        <div ref={root} className={cn('relative overflow-hidden', className)}>
            <div
                ref={inner}
                className={cn('absolute inset-x-0 will-change-transform', innerClassName)}
                style={{ top: `-${amount}%`, bottom: `-${amount}%` }}
            >
                {children}
            </div>
        </div>
    )
}

export default Parallax
